import { useCallback, useState } from "react";
import PropTypes from "prop-types";
import api from "api/api";

// mui components & icons
import { Box, Typography, Button, IconButton } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import FormControlLabel from "@mui/material/FormControlLabel";
import FormGroup from "@mui/material/FormGroup";
import Checkbox from "@mui/material/Checkbox";

// redux
import { useDispatch } from "react-redux";
import { showNotification } from "store/features/notificationSlice";

// common components
import Loader from "../common/Loader";

function UserProfileDelete({ isOpen = false, setIsOpen = () => {}, selectedUserProfiles = () => {}, getUserProfiles = () => {} }) {
    const dispatch = useDispatch();
    const [isLoading, setIsLoading] = useState(false);
    const [selectedIds, setSelectedIds] = useState([]);

    // Handle error function
    const handleError = useCallback(
        (type, error) => {
            dispatch(showNotification({ severity: type, data: error }));
        },
        [dispatch]
    );

    const handleSelectAll = (event) => {
        setSelectedIds(event.target.checked ? selectedUserProfiles.map((profile) => profile.id) : []);
    };

    const handleChange = (id) => () => {
        setSelectedIds((prev) => (prev.includes(id) ? prev.filter((selectedId) => selectedId !== id) : [...prev, id]));
    };

    const handleReset = () => {
        setSelectedIds([]);
    };

    const handleClose = () => {
        setIsOpen(false);
        handleReset();
    };

    const returnSelectAll = () => {
        return (
            <FormControlLabel
                sx={{
                    "& .MuiTypography-root": {
                        fontSize: 12,
                    },
                }}
                style={{
                    margin: "0px 0px 0px 40px",
                    padding: "0px 0px",
                    textAlign: "left",
                }}
                control={
                    <Checkbox
                        sx={{
                            "& .MuiSvgIcon-root": {
                                fontSize: 18,
                            },
                        }}
                        color="primary"
                        checked={selectedIds.length === selectedUserProfiles.length}
                        onChange={(event) => handleSelectAll(event)}
                    />
                }
                label="Select All"
            />
        );
    };

    const returnCheckBoxes = (profile) => {
        const id = profile.id;
        const label = profile.title;
        return (
            <FormControlLabel
                key={label}
                sx={{
                    "& .MuiTypography-root": {
                        fontSize: 12,
                    },
                }}
                style={{
                    margin: "0px 0px 0px 52px",
                    padding: "0px 0px",
                    textAlign: "left",
                }}
                control={
                    <Checkbox
                        sx={{
                            "& .MuiSvgIcon-root": {
                                fontSize: 18,
                            },
                        }}
                        color="primary"
                        id={`${id}`}
                        checked={selectedIds.includes(id)}
                        onChange={handleChange(id)}
                        value={label}
                    />
                }
                label={label.replace(/ \|/g, ",").trim()}
            />
        );
    };

    const handleDelete = async () => {
        setIsLoading(true);
        const requests = selectedIds.map((id) => {
            const url = process.env.REACT_APP_SERVER_PATH + `/user-profile-infos/${id}/`;
            return api.delete(url);
        });

        try {
            await Promise.all(requests)
                .then(() => {
                    getUserProfiles();
                    setIsLoading(false);
                    handleClose();
                    dispatch(showNotification({ severity: "success", data: "user profiles Deleted successfully." }));
                })
                .catch((error) => {
                    handleError("error", error);
                    setIsLoading(false);
                });
        } catch (error) {
            handleError("error", error);
            setIsLoading(false);
        }
    };

    return (
        <>
            {isLoading && <Loader />}
            {/* Dialog */}
            {isOpen && (
                <>
                    <Box
                        sx={{
                            position: "absolute",
                            inset: 0,
                            zIndex: 9,
                            backgroundColor: "rgba(0, 0, 0, 0.18)",
                        }}
                    />
                    <Box
                        role="dialog"
                        aria-modal="true"
                        sx={{
                            position: "absolute",
                            top: "20%",
                            bottom: "20%",
                            left: "20%",
                            right: "20%",
                            zIndex: 10,
                            display: "flex",
                            flexDirection: "column",
                            backgroundColor: "#FFFFFF",
                            borderRadius: "6px",
                            boxShadow: "0px 8px 24px rgba(0, 0, 0, 0.18)",
                            overflow: "auto",
                        }}
                    >
                        {/* Dialog Header */}
                        <Box
                            sx={{
                                backgroundColor: "rgb(51, 94, 234,0.1)",
                                color: "rgb(51, 94, 234)",
                                fontSize: "1.25rem",
                                minHeight: "56px",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                padding: "10px 12px",
                                position: "relative",
                            }}
                        >
                            <Typography
                                variant="h6"
                                sx={{
                                    color: "#005EEF",
                                    fontWeight: "bold",
                                }}
                            >
                                Delete User Profile
                            </Typography>

                            <IconButton
                                size="small"
                                onClick={handleClose}
                                aria-label="Close"
                                sx={{
                                    position: "absolute",
                                    right: "16px",
                                    color: "#005EEF",
                                }}
                            >
                                <CloseIcon fontSize="small" />
                            </IconButton>
                        </Box>

                        {/* Dialog Content */}
                        <Box
                            sx={{
                                flex: 1,
                                padding: "20px",
                            }}
                        >
                            <div style={{ marginTop: "10px" }}>
                                <h1 style={{ textAlign: "center", fontSize: "1rem" }}>Please select the User Profiles you want to delete</h1>
                            </div>
                            <FormGroup>
                                {returnSelectAll()}
                                {selectedUserProfiles.map((profile) => {
                                    return returnCheckBoxes(profile);
                                })}
                            </FormGroup>
                        </Box>

                        {/* Delete Confirmation Note */}
                        <h1 style={{ textAlign: "center", fontSize: "0.9rem", fontWeight: "bold", marginBottom: "0px" }}>
                            Are you sure you want to delete the selected <br /> User Profile(s)
                        </h1>

                        {/* Dialog Actions */}
                        <Box
                            sx={{
                                minHeight: "40px",
                                display: "flex",
                                justifyContent: "center",
                                alignItems: "center",
                                padding: "0px 20px 20px 20px",
                            }}
                        >
                            <Button
                                size="small"
                                className="custom-btn custom-btn--upload-reset px-4"
                                variant="contained"
                                color="error"
                                onClick={handleDelete}
                                disabled={selectedIds.length === 0}
                            >
                                Delete
                            </Button>
                            <Button
                                size="small"
                                className="custom-btn custom-btn--upload-reset"
                                variant="outlined"
                                onClick={handleReset}
                            >
                                Reset
                            </Button>
                        </Box>
                    </Box>
                </>
            )}
        </>
    );
}

// Typechecking props for the UserProfileDelete
UserProfileDelete.propTypes = {
    isOpen: PropTypes.bool,
    setIsOpen: PropTypes.func,
    selectedUserProfiles: PropTypes.array,
    getUserProfiles: PropTypes.func,
};

export default UserProfileDelete;
